import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Wine, Clock, CheckCircle, ArrowLeft, Volume2, VolumeX, RefreshCw, ChefHat
} from 'lucide-react';
import { playHotelBellSound } from '../../hooks/useOrderNotifications';
import useSmartPolling from '../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BarPanel = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // Refs avoid useCallback dependency churn that was tearing down the interval
  // on every poll.
  const lastOrderCountRef = useRef(0);
  const lastKitchenReadyIdsRef = useRef(new Set());
  const soundEnabledRef = useRef(true);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/pedidos/barra`);
      if (response.ok) {
        const data = await response.json();

        const pendingCount = data.filter(o => o.estado === 'pendiente').length;
        if (pendingCount > lastOrderCountRef.current && lastOrderCountRef.current > 0 && soundEnabledRef.current) {
          playHotelBellSound();
        }
        lastOrderCountRef.current = pendingCount;

        const currentKitchenReadyIds = new Set(
          data.filter(o => o.has_kitchen_ready).map(o => o.id)
        );
        const newKitchenReady = [...currentKitchenReadyIds].filter(id => !lastKitchenReadyIdsRef.current.has(id));
        if (newKitchenReady.length > 0 && lastKitchenReadyIdsRef.current.size > 0 && soundEnabledRef.current) {
          playHotelBellSound();
          setTimeout(() => playHotelBellSound(), 400);
        }
        lastKitchenReadyIdsRef.current = currentKitchenReadyIds;
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useSmartPolling(fetchOrders, 10000);

  const updateOrderStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      const order = [...pendingOrders, ...preparingOrders, ...readyOrders].find(o => o.id === orderId);

      if (order && order.items) {
        // Parallel item updates (was sequential await — slow on big orders).
        const updates = order.items
          .map((item, i) => ({
            idx: item.original_index !== undefined ? item.original_index : i,
            estado: item.estado,
          }))
          .filter(it => it.estado !== newStatus)
          .map(it => fetch(`${API_URL}/api/pedidos/${orderId}/item/${it.idx}/estado?status=${newStatus}&source=barra`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }));
        await Promise.all(updates);
      }

      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  // Special function for bar to mark kitchen items as delivered (waiter delivers food)
  const markKitchenItemsDelivered = async (orderId) => {
    const token = localStorage.getItem('admin_token');
    try {
      // Find the order to get kitchen items
      const order = [...pendingOrders, ...preparingOrders, ...readyOrders].find(o => o.id === orderId);
      
      if (order && order.kitchen_items) {
        // Mark all kitchen items as entregado using their original_index
        for (const item of order.kitchen_items) {
          const itemIndex = item.original_index;
          if (itemIndex !== undefined && item.estado !== 'entregado') {
            await fetch(`${API_URL}/api/pedidos/${orderId}/item/${itemIndex}/estado?status=entregado&source=barra`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });
          }
        }
      }
      
      fetchOrders();
    } catch (error) {
      console.error('Error marking kitchen items delivered:', error);
    }
  };

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'pendiente': return 'bg-purple-500';
      case 'preparando': return 'bg-blue-500';
      case 'listo': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'pendiente': return 'NUEVO';
      case 'preparando': return 'SIRVIENDO';
      case 'listo': return 'LISTO';
      default: return estado;
    }
  };

  const getTimeAgo = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins} min`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  const pendingOrders = orders.filter(o => o.estado === 'pendiente');
  const preparingOrders = orders.filter(o => o.estado === 'preparando');
  const readyOrders = orders.filter(o => o.estado === 'listo');

  return (
    <div className="min-h-screen bg-stone-900 text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-stone-800 rounded-lg"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Wine size={32} className="text-purple-500" />
            <div>
              <h1 className="text-2xl font-bold">BARRA</h1>
              <p className="text-stone-400 text-sm">Centro de servicio</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-3 rounded-lg ${soundEnabled ? 'bg-green-600' : 'bg-stone-700'}`}
            title={soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}
          >
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          <button
            onClick={fetchOrders}
            className="p-3 bg-stone-700 hover:bg-stone-600 rounded-lg"
          >
            <RefreshCw size={20} />
          </button>
          {pendingOrders.length > 0 && (
            <Badge className="bg-purple-500 text-white text-lg px-3 py-1 animate-pulse">
              {pendingOrders.length} NUEVOS
            </Badge>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw size={32} className="animate-spin text-stone-500" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-stone-500">
          <Wine size={64} className="mb-4 opacity-50" />
          <p className="text-xl">Sin pedidos pendientes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* PENDIENTES */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
              <h2 className="text-lg font-semibold text-purple-400">PENDIENTES ({pendingOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {pendingOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onStatusChange={updateOrderStatus}
                  onMarkKitchenDelivered={markKitchenItemsDelivered}
                  getTimeAgo={getTimeAgo}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  nextStatus="preparando"
                  nextLabel="PREPARAR"
                />
              ))}
            </div>
          </div>

          {/* EN PREPARACIÓN */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <h2 className="text-lg font-semibold text-blue-400">SIRVIENDO ({preparingOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {preparingOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onStatusChange={updateOrderStatus}
                  onMarkKitchenDelivered={markKitchenItemsDelivered}
                  getTimeAgo={getTimeAgo}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  nextStatus="listo"
                  nextLabel="SERVIDO"
                />
              ))}
            </div>
          </div>

          {/* LISTOS */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <h2 className="text-lg font-semibold text-green-400">ENTREGADOS ({readyOrders.length})</h2>
            </div>
            <div className="space-y-4">
              {readyOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onStatusChange={updateOrderStatus}
                  onMarkKitchenDelivered={markKitchenItemsDelivered}
                  getTimeAgo={getTimeAgo}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  nextStatus="entregado"
                  nextLabel="COMPLETAR"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderCard = ({ order, onStatusChange, onMarkKitchenDelivered, getTimeAgo, getStatusColor, getStatusLabel, nextStatus, nextLabel }) => {
  const hasKitchenReady = order.has_kitchen_ready;
  const isKitchenMixed = order.kitchen_items_count > 0;
  const kitchenItems = order.kitchen_items || [];
  const hasBarItems = order.items && order.items.length > 0;
  const isPureKitchenOrder = !hasBarItems && kitchenItems.length > 0;
  
  return (
    <Card className={`bg-stone-800 border-stone-700 ${hasKitchenReady ? 'kitchen-ready-glow' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-white">#{order.mesa_numero}</span>
            <Badge className={`${getStatusColor(order.estado)} text-white`}>
              {getStatusLabel(order.estado)}
            </Badge>
            {isPureKitchenOrder && (
              <Badge variant="outline" className="text-orange-400 border-orange-400 text-xs">
                <ChefHat size={12} className="mr-1" />
                Solo cocina
              </Badge>
            )}
            {hasKitchenReady && (
              <Badge className="kitchen-ready-badge text-white text-xs px-2 py-1 flex items-center gap-1">
                <ChefHat size={12} />
                COCINA LISTA!
              </Badge>
            )}
            {isKitchenMixed && !hasKitchenReady && !isPureKitchenOrder && (
              <Badge variant="outline" className="text-amber-400 border-amber-400 text-xs">
                <ChefHat size={12} className="mr-1" />
                {order.kitchen_items_ready}/{order.kitchen_items_count} cocina
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-stone-400">
            <Clock size={14} />
            <span className="text-sm">{getTimeAgo(order.created_at)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Bar items */}
        {hasBarItems && (
          <ul className="space-y-2 mb-4">
            {order.items.map((item, idx) => (
              <li key={idx} className="flex justify-between items-start">
                <div>
                  <span className="text-white font-medium">{item.quantity}x {item.product_name}</span>
                  {item.options?.length > 0 && (
                    <p className="text-stone-400 text-sm">{item.options.join(', ')}</p>
                  )}
                  {item.notes && (
                    <p className="text-purple-400 text-sm">Nota: {item.notes}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        
        {/* Kitchen items section */}
        {kitchenItems.length > 0 && (
          <div className={`mb-4 p-3 rounded-lg border ${hasKitchenReady ? 'bg-orange-900/30 border-orange-600' : 'bg-stone-900/50 border-stone-600'}`}>
            <p className="text-xs font-semibold text-orange-400 mb-2 flex items-center gap-1">
              <ChefHat size={14} />
              {isPureKitchenOrder ? 'ITEMS DE COCINA' : `ITEMS DE COCINA (${order.kitchen_items_ready}/${order.kitchen_items_count} listos)`}
            </p>
            <ul className="space-y-1">
              {kitchenItems.map((item, idx) => (
                <li key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-stone-300">{item.quantity}x {item.product_name}</span>
                  <Badge className={`text-xs ${
                    item.estado === 'listo' ? 'bg-green-600' :
                    item.estado === 'preparando' ? 'bg-blue-600' :
                    item.estado === 'entregado' ? 'bg-gray-600' :
                    'bg-stone-600'
                  }`}>
                    {item.estado || 'pendiente'}
                  </Badge>
                </li>
              ))}
            </ul>
            {hasKitchenReady && (
              <Button 
                onClick={() => onMarkKitchenDelivered(order.id)}
                className="w-full mt-3 bg-orange-600 hover:bg-orange-700"
                size="sm"
              >
                <CheckCircle size={16} className="mr-2" />
                Marcar cocina ENTREGADO
              </Button>
            )}
          </div>
        )}
        
        {order.notes && (
          <p className="text-purple-400 text-sm mb-3 p-2 bg-purple-900/30 rounded">
            {order.notes}
          </p>
        )}
        
        {/* Action button - only for bar items */}
        {hasBarItems && (
          <Button 
            onClick={() => onStatusChange(order.id, nextStatus)}
            className={`w-full ${
              nextStatus === 'preparando' ? 'bg-blue-600 hover:bg-blue-700' :
              nextStatus === 'listo' ? 'bg-green-600 hover:bg-green-700' :
              'bg-stone-600 hover:bg-stone-500'
            }`}
          >
            <CheckCircle size={18} className="mr-2" />
            {nextLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default BarPanel;
